import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { Field, TextArea } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { fmtDateTime, REVIEW_STATUS } from '@/lib/labels'

type ReviewRow = {
  id: string
  rating: number
  body: string
  status: string
  createdAt: string
  product: { id: string; sku: string; name: string }
  customer: { id: string; firstName: string; email: string } | null
}

const TABS = [
  { id: 'PENDING', label: 'En attente' },
  { id: 'PUBLISHED', label: 'Publiés' },
  { id: 'REJECTED', label: 'Refusés' },
  { id: 'NEEDS_INFO', label: 'Précision' },
  { id: '', label: 'Tous' },
]

type Action = { type: 'publish' | 'reject' | 'query'; id: string }

export function Avis() {
  const [status, setStatus] = useState('PENDING')
  const [action, setAction] = useState<Action | null>(null)
  const [note, setNote] = useState('')
  const toast = useToast()
  const qc = useQueryClient()

  const reviews = useQuery({
    queryKey: ['admin-reviews', status],
    queryFn: () => {
      const q = status ? `?status=${status}&limit=100` : '?limit=100'
      return api<{ items: ReviewRow[] }>(`/admin/reviews${q}`)
    },
  })

  const run = useMutation({
    mutationFn: async () => {
      if (!action) return
      if (action.type === 'publish') {
        return apiMutate(`/admin/reviews/${action.id}/publish`, 'POST')
      }
      if (action.type === 'reject') {
        return apiMutate(`/admin/reviews/${action.id}/reject`, 'POST', { note })
      }
      return apiMutate(`/admin/reviews/${action.id}/query`, 'POST', { note })
    },
    onSuccess: () => {
      toast.ok(
        action?.type === 'publish'
          ? 'Avis publié'
          : action?.type === 'reject'
            ? 'Avis refusé'
            : 'Précision demandée',
      )
      setAction(null)
      setNote('')
      void qc.invalidateQueries({ queryKey: ['admin-reviews'] })
      void qc.invalidateQueries({ queryKey: ['admin-badges'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = reviews.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id || 'all'}
            type="button"
            onClick={() => setStatus(t.id)}
            className={`kicker rounded-pilule px-3 py-1.5 ${
              status === t.id
                ? 'bg-encre-admin text-creme'
                : 'border border-bordure-admin text-encre-admin'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {reviews.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Aucun avis" body="Rien pour ce filtre." />
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((r) => {
            const st = REVIEW_STATUS[r.status] ?? { label: r.status, tone: 'neutral' }
            const canModerate = r.status === 'PENDING' || r.status === 'NEEDS_INFO'
            return (
              <li key={r.id} className="carte-admin p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-encre-admin">{r.product.name}</p>
                    <p className="text-xs text-second-admin">
                      {r.customer
                        ? `${r.customer.firstName} · ${r.customer.email}`
                        : 'Anonyme'}{' '}
                      · {r.rating}/5 · {fmtDateTime(r.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill label={st.label} tone={st.tone} />
                    {canModerate ? (
                      <RowActions
                        items={[
                          {
                            label: 'Publier',
                            onSelect: () => setAction({ type: 'publish', id: r.id }),
                          },
                          {
                            label: 'Refuser',
                            danger: true,
                            onSelect: () => setAction({ type: 'reject', id: r.id }),
                          },
                          {
                            label: 'Demander précision',
                            onSelect: () => setAction({ type: 'query', id: r.id }),
                          },
                        ]}
                      />
                    ) : null}
                  </div>
                </div>
                <p className="mt-3 text-sm text-encre-admin">{r.body}</p>
              </li>
            )
          })}
        </ul>
      )}

      <ConfirmDialog
        open={Boolean(action)}
        title={
          action?.type === 'publish'
            ? 'Publier cet avis ?'
            : action?.type === 'reject'
              ? 'Refuser cet avis ?'
              : 'Demander une précision ?'
        }
        body={
          action?.type === 'publish'
            ? 'L’avis sera visible sur la fiche produit.'
            : 'Vous pouvez laisser une note pour le modérateur.'
        }
        confirmLabel="Confirmer"
        danger={action?.type === 'reject'}
        busy={run.isPending}
        onClose={() => {
          setAction(null)
          setNote('')
        }}
        onConfirm={() => run.mutate()}
      />

      {action && action.type !== 'publish' ? (
        <div className="fixed inset-x-0 bottom-20 z-[55] mx-auto w-full max-w-lg px-4">
          <div className="carte-admin p-3 shadow-mega">
            <Field label="Note (optionnel)">
              <TextArea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
            </Field>
          </div>
        </div>
      ) : null}
    </div>
  )
}
